import { useMemo, useState } from "react";
import { apiJobs } from "@/api/recruit/job.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiJobSavePayload, ApiJobsFilter, JobPriority } from "@/types/recruit/job.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const jobsKeys = createQueryKeys("jobs", {
  search: (filter: ApiJobsFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiJobs.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  details: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Job id is required");
      const result = await apiJobs.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  detailsHistory: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Job id is required");
      const result = await apiJobs.getHistory(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  byListOfId: ({
    ids,
    includeCandidatesCount = false,
  }: {
    ids: number[];
    includeCandidatesCount?: boolean;
  }) => ({
    queryKey: [ids, includeCandidatesCount],
    queryFn: async () => {
      const result = await apiJobs.getByListOfId(ids, includeCandidatesCount);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  recruitersSearch: null,
  approvalPolicies: null,
  stakeholdersSearch: null,
});

export const useJobsSearch = (filter: ApiJobsFilter, enabled = true) => {
  return useQuery({
    ...jobsKeys.search(filter),
    enabled: enabled,
  });
};

export const useJobDetails = (id: number | undefined) => {
  return useQuery({
    ...jobsKeys.details(id),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
};

export interface SaveParams {
  id: number;
  payload: ApiJobSavePayload;
}

export const useJobSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
  fieldMap: Record<string, string> = {}
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: SaveParams) {
      const result = id == 0 ? await apiJobs.create(payload) : await apiJobs.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (job) => {
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });

      queryClient.setQueryData(jobsKeys.details(job.id).queryKey, job);
    },
    onError: (error: Error) => {
      onError(error, control, fieldMap);
    },
  });
};

export const useJobRecruitersSearch = (enabled: boolean = true) => {
  return useQuery({
    ...jobsKeys.recruitersSearch,
    queryFn: async ({ signal }) => {
      const result = await apiJobs.recruitersSearch(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled: enabled,
  });
};

export const useJobsByIds = ({
  ids,
  enabled = true,
  includeCandidatesCount = false,
}: {
  ids: number[];
  enabled?: boolean;
  includeCandidatesCount?: boolean;
}) => {
  return useQuery({
    ...jobsKeys.byListOfId({ ids, includeCandidatesCount }),
    enabled: enabled && ids.length > 0,
  });
};

export const useJobsApprovalPolicies = (enabled: boolean = true) => {
  return useQuery({
    ...jobsKeys.approvalPolicies,
    queryFn: async () => {
      const result = await apiJobs.approvalPolicies();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export const useJobStakeholdersSearch = (enabled: boolean = true) => {
  return useQuery({
    ...jobsKeys.stakeholdersSearch,
    queryFn: async ({ signal }) => {
      const result = await apiJobs.stakeholdersSearch(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled: enabled,
  });
};

export const useJobPatchPriority = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, priority }: { id: number; priority: JobPriority | null }) => {
      const result = await apiJobs.patchPriority(id, priority);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (job) => {
      queryClient.setQueryData(jobsKeys.details(job.id).queryKey, job);
      queryClient.invalidateQueries({ queryKey: jobsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: jobsKeys.byListOfId._def });
    },
    onError: (error: Error) => onError(error),
  });
};

export const useJobDuplicate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const result = await apiJobs.duplicate(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (job) => {
      queryClient.setQueryData(jobsKeys.details(job.id).queryKey, job);
      queryClient.invalidateQueries({ queryKey: jobsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: jobsKeys.byListOfId._def });
    },
    onError: (error: Error) => onError(error),
  });
};

export const useJobDetailsHistory = (id: number | undefined) => {
  return useQuery({
    ...jobsKeys.detailsHistory(id),
    enabled: !!id,
  });
};

export const useJobsSearchInline = (enabled: boolean = true) => {
  const [filter, setFilter] = useState<ApiJobsFilter>({
    page_number: 1,
    status_list: [],
    text: undefined,
  });
  const { data, isLoading } = useJobsSearch(filter, enabled);
  const options = useMemo(() => data?.items ?? [], [data]);

  function search(text: string | undefined) {
    setFilter((prev) => ({ ...prev, text }));
  }

  return { data, isLoading, options, search };
};
